import { useState, useEffect } from 'react';
import Button from 'components/Button';
import LoaderIcon from 'assets/icons/loader.svg';
import CheckmarkIcon from 'assets/icons/checkmark.svg';
import { useSubmitData } from 'hooks/useSubmitData';
import { useAppSelector, useAppDispatch } from 'store';
import { setIsRequiredInfoPopupOpen } from 'store/chartForms/chartForms.slice';
import { buildLocalData } from 'utils/helpers/buildLocalData';
import { avgDealSizeField, currentArrField } from 'utils/constants/fields';
import { FieldValues } from 'interfaces/constants/CalculateModalConfig';
import { ChartFieldBase } from 'utils/constants/chartsForm';
import { useAuth } from 'context/AuthContext';

interface ApplyButtonProps {
  showNestedFields: boolean;
  xAxisSwitch: boolean;
  fieldsValues: FieldValues;
  chartConfig: ChartFieldBase;
}

const ApplyButton = ({
  showNestedFields,
  xAxisSwitch,
  fieldsValues,
  chartConfig
}: ApplyButtonProps) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();
  const { submitData, isSubmitting } = useSubmitData(isAuthenticated);
  const [buttonState, setButtonState] = useState<
    'normal' | 'loading' | 'success'
  >('normal');

  const companyInfo = useAppSelector(state => state.companyInfo);

  useEffect(() => {
    if (buttonState === 'success') {
      const timer = setTimeout(() => {
        setButtonState('normal');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [buttonState]);

  const handleApply = async () => {
    if (companyInfo.requiredInfo.status === null) {
      dispatch(setIsRequiredInfoPopupOpen(true));
    } else {
      setButtonState('loading');
      const otherInfo = {
        companyStatus: companyInfo.requiredInfo.status,
        email: companyInfo.email,
        companyWebsite: companyInfo.companyDetails.companyWebsite,
        companyColor: companyInfo.companyDetails.companyColor,
        companyLogoURL: companyInfo.companyDetails.companyLogoURL
      };
      const localData = buildLocalData(fieldsValues, otherInfo);
      try {
        await submitData(localData);
        setButtonState('success');
      } catch (error) {
        setButtonState('normal');
      }
    }
  };

  const isApplyDisabled = () => {
    const firstFieldName = xAxisSwitch
      ? currentArrField.name
      : avgDealSizeField.name;
    const isFirstFieldEmpty =
      fieldsValues[firstFieldName] === null ||
      fieldsValues[firstFieldName] === '';

    const isAnyChartFormFieldEmpty = chartConfig.calculatedFields.some(
      ({ field }) =>
        fieldsValues[field.name] === null || fieldsValues[field.name] === ''
    );

    return isFirstFieldEmpty || isAnyChartFormFieldEmpty;
  };

  const getButtonLabel = () => {
    switch (buttonState) {
      case 'loading':
        return 'Applying';
      case 'success':
        return 'Saved';
      default:
        return 'Apply';
    }
  };

  const getButtonPrefix = () => {
    switch (buttonState) {
      case 'loading':
        return (
          <img
            src={LoaderIcon}
            alt='Loading'
            className='w-4 h-4 mr-1 animate-spin text-white'
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        );
      case 'success':
        return (
          <div className='w-4 h-4 mr-1 bg-white rounded-full flex items-center justify-center'>
            <img src={CheckmarkIcon} alt='Success' className='w-2.5 h-2.5' />
          </div>
        );
      default:
        return null;
    }
  };

  const getButtonClassName = () => {
    const baseClasses = `flex justify-center items-center rounded-5xl ${
      showNestedFields ? 'w-full' : 'flex-1'
    } h-8 font-semibold text-base transition-all duration-300`;

    switch (buttonState) {
      case 'loading':
        return `${baseClasses} bg-purple-700 text-white disabled:cursor-not-allowed`;
      case 'success':
        return `${baseClasses} bg-green-700 text-white disabled:cursor-not-allowed`;
      default:
        return `${baseClasses} bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed`;
    }
  };

  return (
    <Button
      label={getButtonLabel()}
      id='apply'
      onClick={handleApply}
      disabled={
        isApplyDisabled() ||
        isSubmitting ||
        buttonState === 'loading' ||
        buttonState === 'success'
      }
      className={getButtonClassName()}
      prefix={getButtonPrefix()}
    />
  );
};

export default ApplyButton;
